import { AutoMappingResult, DataImportCellError, DataImportColumnMapping, DataImportFieldType, IDataImportField } from "../DataImport.interfaces";

export function getRequiredFieldsWithoutMapping(fields: IDataImportField[], mappings: DataImportColumnMapping[]): IDataImportField[] {
  return fields.filter((field) => {
    if (!field.required) return false;

    const mapping = mappings.find((item) => item.fieldKey === field.key);

    return !mapping?.csvHeaders || mapping.csvHeaders.length === 0;
  });
}

export function isManualMappingStatus(matchedBy: string): boolean {
  return matchedBy.toLowerCase().includes("manual");
}

export function isEmpty(value: unknown): boolean {
  return value === undefined || value === null || String(value).trim() === "";
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function isBooleanLike(value: unknown): boolean {
  return ["true", "false", "1", "0", "yes", "no", "si"].includes(String(value).trim().toLowerCase());
}

export function formatValue(value: unknown): string {
  if (value === undefined || value === null) return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function getConfidenceColor(confidence: number): "success" | "warning" | "error" {
  if (confidence >= 0.9) return "success";
  if (confidence >= 0.72) return "warning";
  return "error";
}

export function validateValueType(value: unknown, field: IDataImportField): string | null {
  if (!field.fieldType) return null;
  if (field.fieldType === DataImportFieldType.Number && Number.isNaN(Number(value))) return "Expected a numeric value.";
  if (field.fieldType === DataImportFieldType.Email && !isValidEmail(String(value))) return "Expected a valid email address.";
  if (field.fieldType === DataImportFieldType.Boolean && !isBooleanLike(value)) return "Expected 'true' or 'false'.";
  if (field.fieldType === DataImportFieldType.Date && Number.isNaN(Date.parse(String(value)))) return "Expected a valid date.";
  return null;
}

export function resolveValueFromMultipleHeaders(
  row: Record<string, unknown>,
  csvHeaders: string[],
  field: IDataImportField,
): unknown {
  const candidateValues = csvHeaders
    .map((header) => row[header])
    .filter((val) => !isEmpty(val));

  if (candidateValues.length === 0) {
    return undefined;
  }

  // Find the first value that is valid according to the field type
  for (const value of candidateValues) {
    const error = validateValueType(value, field);
    if (!error) {
      return value;
    }
  }

  // If none are valid, check if required
  if (field.required) {
    return candidateValues[0];
  }

  return undefined;
}

export function validateMappedRows(
  rawRows: Record<string, unknown>[],
  fields: IDataImportField[],
  mappings: DataImportColumnMapping[],
): DataImportCellError[] {
  const errors: DataImportCellError[] = [];

  rawRows.forEach((rawRow, rowIndex) => {
    const mappedRow: Record<string, unknown> = {};

    fields.forEach((field) => {
      const mapping = mappings.find((item) => item.fieldKey === field.key);
      const csvHeaders = mapping?.csvHeaders ?? [];
      const value = csvHeaders.length > 0 ? resolveValueFromMultipleHeaders(rawRow, csvHeaders, field) : undefined;
      mappedRow[field.key] = value;

      if (field.required && isEmpty(value)) {
        errors.push({ rowIndex, fieldKey: field.key, fieldLabel: field.label, message: "This field is required.", value });
        return;
      }
      if (!isEmpty(value)) {
        const typeError = validateValueType(value, field);
        if (typeError && field.required) {
          errors.push({ rowIndex, fieldKey: field.key, fieldLabel: field.label, message: typeError, value });
          return;
        }
      }
      if (field.validate && !isEmpty(value) && field.required) {
        const customError = field.validate(value, mappedRow);
        if (customError) {
          errors.push({ rowIndex, fieldKey: field.key, fieldLabel: field.label, message: customError, value });
        }
      }
    });
  });

  return errors;
}

export function calculateHeaderScore(csvHeader: string, expectedHeader: string): { csvHeader: string; confidence: number; matchedBy: string } {
  const normalizedCsv = normalizeHeader(csvHeader);
  const normalizedExpected = normalizeHeader(expectedHeader);

  if (normalizedCsv === normalizedExpected) return { csvHeader, confidence: 1, matchedBy: "exact normalized" };
  if (normalizedCsv.includes(normalizedExpected) || normalizedExpected.includes(normalizedCsv))
    return { csvHeader, confidence: 0.88, matchedBy: "contains" };

  const similarity = diceCoefficient(normalizedCsv, normalizedExpected);
  return { csvHeader, confidence: similarity, matchedBy: "fuzzy" };
}

export function normalizeHeader(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[_-]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[^a-z0-9 ]/g, "")
    .replace(/\s+/g, "");
}

export function diceCoefficient(first: string, second: string): number {
  if (first === second) return 1;
  if (first.length < 2 || second.length < 2) return 0;
  const firstBigrams = buildBigrams(first);
  const secondBigrams = buildBigrams(second);
  let intersection = 0;
  const secondBigramCounts = new Map<string, number>();

  secondBigrams.forEach((bigram) => {
    secondBigramCounts.set(bigram, (secondBigramCounts.get(bigram) ?? 0) + 1);
  });
  firstBigrams.forEach((bigram) => {
    const count = secondBigramCounts.get(bigram) ?? 0;
    if (count > 0) {
      intersection += 1;
      secondBigramCounts.set(bigram, count - 1);
    }
  });

  return (2 * intersection) / (firstBigrams.length + secondBigrams.length);
}

export function buildBigrams(value: string): string[] {
  const bigrams: string[] = [];
  for (let index = 0; index < value.length - 1; index += 1) {
    bigrams.push(value.slice(index, index + 2));
  }
  return bigrams;
}

export function findBestHeaderMatch(field: IDataImportField, csvHeaders: string[], usedHeaders: Set<string>): AutoMappingResult {
  const candidates = csvHeaders.filter((header) => !usedHeaders.has(header));
  const fieldCandidates = [field.key, field.label, ...(field.aliases ?? [])];

  const matches: { header: string; confidence: number; matchedBy: string }[] = [];

  for (const header of candidates) {
    let bestScoreForHeader = { confidence: 0, matchedBy: "none" };
    for (const fieldCandidate of fieldCandidates) {
      const score = calculateHeaderScore(header, fieldCandidate);
      if (score.confidence > bestScoreForHeader.confidence) {
        bestScoreForHeader = { confidence: score.confidence, matchedBy: score.matchedBy };
      }
    }
    if (bestScoreForHeader.confidence >= 0.72) {
      matches.push({
        header,
        confidence: bestScoreForHeader.confidence,
        matchedBy: bestScoreForHeader.matchedBy,
      });
    }
  }

  // Sort by confidence descending
  matches.sort((a, b) => b.confidence - a.confidence);

  if (matches.length === 0) {
    return { csvHeaders: [], confidence: 0, matchedBy: "manual required" };
  }

  return {
    csvHeaders: matches.map((m) => m.header),
    confidence: matches[0].confidence,
    matchedBy: matches[0].matchedBy,
  };
}

export function normalizePapaRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return rows.filter((row) => {
    return Object.values(row).some((value) => {
      return value !== undefined && value !== null && String(value).trim() !== "";
    });
  });
}

export function extractHeadersFromRows(rows: Record<string, unknown>[]): string[] {
  const headers = new Set<string>();
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => headers.add(key));
  });
  return Array.from(headers);
}

export function buildInitialMappings(fields: IDataImportField[], csvHeaders: string[]): DataImportColumnMapping[] {
  const usedHeaders = new Set<string>();

  return fields.map((field) => {
    const bestMatch = findBestHeaderMatch(field, csvHeaders, usedHeaders);
    if (bestMatch.csvHeaders.length > 0) {
      bestMatch.csvHeaders.forEach((h) => usedHeaders.add(h));
    }
    return {
      fieldKey: field.key,
      csvHeaders: bestMatch.csvHeaders,
      confidence: bestMatch.confidence,
      matchedBy: bestMatch.matchedBy,
    };
  });
}
